import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useNotify, errorMessage } from '../components/Toast';
import { IconEdit, IconPower, IconTrash } from '../components/Icons';
import '../components/AdminUI.css';
import './Common.css';

const Users = () => {
  const notify = useNotify();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role: 'user',
    status: 'active'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users', {
        params: { search: searchTerm, per_page: 100 },
      });
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`/users/${editingUser.id}`, formData);
      } else {
        await axios.post('/users', formData);
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', password_confirmation: '', role: 'user', status: 'active' });
      fetchUsers();
    } catch (error) {
      notify.error(errorMessage(error, "Erreur lors de l'enregistrement de l'utilisateur"));
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ ...user, password: '', password_confirmation: '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cet utilisateur ?')) {
      try {
        await axios.delete(`/users/${id}`);
        fetchUsers();
      } catch (error) {
        notify.error(errorMessage(error, "Erreur lors de la suppression de l'utilisateur"));
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await axios.patch(`/users/${id}/toggle-status`, {
        status: currentStatus === 'active' ? 'inactive' : 'active'
      });
      fetchUsers();
    } catch (error) {
      notify.error(errorMessage(error, 'Erreur lors de la mise à jour du statut'));
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-left">
          <h2>Utilisateurs</h2>
          <p>Gérer les utilisateurs de la plateforme</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowModal(true); setEditingUser(null); setFormData({ name: '', email: '', password: '', password_confirmation: '', role: 'user', status: 'active' }); }}>
          + Ajouter Utilisateur
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Rechercher par nom ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
        />
        <button className="btn btn-secondary" onClick={fetchUsers}>Rechercher</button>
      </div>

      {loading ? (
        <div className="loading-state">Chargement...</div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Statut</th>
                <th>Créé le</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td><strong>{user.name}</strong></td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`status-badge ${user.role === 'admin' ? 'active' : 'draft'}`}>
                      {user.role === 'admin' ? 'Admin' : 'Utilisateur'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.status === 'active' ? 'active' : 'inactive'}`}>
                      {user.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-icon-btn" title="Modifier" aria-label="Modifier" onClick={() => handleEdit(user)}>
                        <IconEdit />
                      </button>
                      <button
                        className={`admin-icon-btn ${user.status === 'active' ? '' : 'is-success'}`}
                        title={user.status === 'active' ? 'Désactiver' : 'Activer'}
                        aria-label={user.status === 'active' ? 'Désactiver' : 'Activer'}
                        onClick={() => handleToggleStatus(user.id, user.status)}
                      >
                        <IconPower />
                      </button>
                      <button className="admin-icon-btn is-danger" title="Supprimer" aria-label="Supprimer" onClick={() => handleDelete(user.id)}>
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? 'Modifier Utilisateur' : 'Ajouter Utilisateur'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nom</label>
                <input placeholder="Entrez le nom de l'utilisateur" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" placeholder="Entrez l'adresse email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Mot de passe {editingUser && '(laisser vide pour conserver)'}</label>
                  <input type="password" placeholder="Entrez le mot de passe" value={formData.password || ''} onChange={(e) => setFormData({...formData, password: e.target.value})} required={!editingUser} />
                </div>
                <div className="form-group">
                  <label>Confirmer le Mot de passe</label>
                  <input type="password" placeholder="Confirmez le mot de passe" value={formData.password_confirmation || ''} onChange={(e) => setFormData({...formData, password_confirmation: e.target.value})} required={!editingUser} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Rôle</label>
                  <select value={formData.role || 'user'} onChange={(e) => setFormData({...formData, role: e.target.value})}>
                    <option value="user">Utilisateur</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select value={formData.status || 'active'} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
